import {
  AgentProfileResponseDto,
  PacingDelayRangeDto,
  SpeechPacingConfigDto,
  TurnHandlingConfigDto,
} from '@shared/services/apiService';
import { AddAgentPromptFormValue } from './types';

/**
 * Recommended baseline presets (the tuned smiles-app profile values). Used by
 * the "Apply defaults" buttons to populate turn-handling / speech-pacing in one
 * click. Values match config/profiles/smiles-app.opa/default.yaml.
 */
export const TURN_HANDLING_DEFAULTS: TurnHandlingConfigDto = {
  minEndpointingDelay: 0.5,
  maxEndpointingDelay: 6.0,
  endpointingMode: 'dynamic',
  interruptionEnabled: true,
  interruptionMode: 'vad',
  minInterruptionWords: 2,
  minInterruptionDuration: 0.8,
  falseInterruptionTimeout: 1.5,
  resumeFalseInterruption: true,
  preemptiveGeneration: {
    enabled: true,
    preemptiveTts: false,
    maxSpeechDuration: 10,
    maxRetries: 3,
  },
};

export const SPEECH_PACING_DEFAULTS: SpeechPacingConfigDto = {
  enabled: true,
  phraseBoundaryDelayMs: { minMs: 180, maxMs: 220 },
  sentenceBoundaryDelayMs: { minMs: 340, maxMs: 420 },
  seed: 'qwen-runtime-pacing-measured-v1',
};

export const mapAPResponseDtoToFormValue = (
  data: AgentProfileResponseDto | undefined,
): AddAgentPromptFormValue | undefined => {
  if (!data) {
    return data;
  }
  return {
    name: data.name,
    prompt: data.prompt || '',
    firstMessage: data.firstMessage ?? '',
    firstMessageInterruptionsEnabled: data.firstMessageInterruptionsEnabled ?? false,
    turnHandling: data.turnHandling ?? undefined,
    speechPacing: data.speechPacing ?? undefined,
  };
};

const isMeaningful = (value: unknown): boolean =>
  value !== undefined &&
  value !== null &&
  value !== '' &&
  !(typeof value === 'number' && Number.isNaN(value));

/**
 * Compact a turn-handling form value: drop empty/NaN fields, prune an empty
 * nested preemptiveGeneration, and return undefined when nothing was set so the
 * assistant inherits agent defaults instead of pinning a fully-defaulted object.
 */
export const cleanTurnHandling = (
  value: TurnHandlingConfigDto | undefined,
): TurnHandlingConfigDto | undefined => {
  if (!value) {
    return undefined;
  }

  const { preemptiveGeneration, ...rest } = value;

  const cleaned: TurnHandlingConfigDto = {};
  (Object.keys(rest) as (keyof typeof rest)[]).forEach((key) => {
    if (isMeaningful(rest[key])) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (cleaned as any)[key] = rest[key];
    }
  });

  if (preemptiveGeneration) {
    const pg: NonNullable<TurnHandlingConfigDto['preemptiveGeneration']> = {};
    (Object.keys(preemptiveGeneration) as (keyof typeof preemptiveGeneration)[]).forEach((key) => {
      if (isMeaningful(preemptiveGeneration[key])) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (pg as any)[key] = preemptiveGeneration[key];
      }
    });
    if (Object.keys(pg).length > 0) {
      cleaned.preemptiveGeneration = pg;
    }
  }

  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
};

const cleanDelay = (
  value: number | PacingDelayRangeDto | undefined,
): number | PacingDelayRangeDto | undefined => {
  if (typeof value === 'number') {
    return isMeaningful(value) ? value : undefined;
  }
  if (value && typeof value === 'object') {
    const { minMs, maxMs } = value;
    if (isMeaningful(minMs) && isMeaningful(maxMs)) {
      return { minMs, maxMs };
    }
  }
  return undefined;
};

/**
 * Speech pacing is opt-in: emit a config only when enabled, carrying the
 * meaningful fields. When not enabled, return undefined so the assistant
 * inherits defaults instead of pinning a disabled config. The seed is required
 * by the API when enabled, so an enabled-without-seed value is intentionally
 * left for the server to reject with a clear error.
 */
export const cleanSpeechPacing = (
  value: SpeechPacingConfigDto | undefined,
): SpeechPacingConfigDto | undefined => {
  if (!value || value.enabled !== true) {
    return undefined;
  }

  const cleaned: SpeechPacingConfigDto = { enabled: true };

  const phrase = cleanDelay(value.phraseBoundaryDelayMs);
  if (phrase !== undefined) {
    cleaned.phraseBoundaryDelayMs = phrase;
  }
  const sentence = cleanDelay(value.sentenceBoundaryDelayMs);
  if (sentence !== undefined) {
    cleaned.sentenceBoundaryDelayMs = sentence;
  }
  if (isMeaningful(value.seed)) {
    cleaned.seed = value.seed;
  }

  return cleaned;
};
